import { Flex, Checkbox } from "@chakra-ui/react";
import colors from "core/resources/theme/colors";
import SelectableButton from "components/Button/SelectableButton";
import InputSearch from "components/Input/InputSearch";
import ExportMenu from "components/ExportMenu";
import { OperacaoEnum } from "core/resources/enums/conta";
import { exportExcelRelatorioDemonstrativoReceitaDespesa } from "../helper/util";
import {
  DemonstrativoReceitaEDespesaItem,
  RelatorioDemonstrativoReceitaDespesaTotais,
  DemonstrativoReceitaEDespesaTransferencia,
} from "features/relatorios/typings";
import useDownloadRenderedPdf from "infra/helpers/useDownloadRenderedPdf";
import moment from "moment";
import PDFRelatorioDemonstrativoReceitaDespesa from "./../../PDFRelatorio";
import { useRelatorioDemonstrativoReceitaDespesaPdf } from "core/features/relatorios/hooks/useRelatorioDemonstrativoReceitaDespesaPdf";
import { FiltroRelatorioReceitaDespesa } from "core/features/relatorios/typings";
import ModalAssinaturasRelatorio from "components/Relatorio/ModalAssinaturas";
import { useState } from "react";
import { AssinaturasItem } from "components/Relatorio/FormAssinaturas";
import { pdf } from "@react-pdf/renderer";
import { mergePdfsFromBlobOrUrlList } from "infra/helpers/mergePdfsFromBlobOrUrl";
import { downloadLocalBlob } from "infra/helpers/download";
import http from "infra/http";
import useGetContasBancarias from "features/contas_bancarias/hooks/useGetContasBancarias";
import { logError } from "infra/packages/@sentry/logError";
import { showError } from "infra/helpers/alerta";
import { useLoading } from "components/GlobalLoading";
import { RelatorioDemonstrativoReceitaDespesaPdfResponse } from "features/relatorios/typings";

export interface HeaderTableProps {
  onSearch: (q?: string) => void;
  value: string;
  operacao?: OperacaoEnum;
  onSelectOperacao?: (operacao: OperacaoEnum | undefined) => any;
  filtro?: FiltroRelatorioReceitaDespesa;
  receitas: DemonstrativoReceitaEDespesaItem[];
  despesas: DemonstrativoReceitaEDespesaItem[];
  totais: RelatorioDemonstrativoReceitaDespesaTotais;
  transferencias?: DemonstrativoReceitaEDespesaTransferencia[];
  somarSubcategorias?: boolean;
  setSomarSubcategorias?: (v: boolean) => any;
}

function HeaderTable({
  onSearch,
  value,
  operacao,
  onSelectOperacao,
  filtro,
  receitas,
  despesas,
  totais,
  transferencias,
  somarSubcategorias,
  setSomarSubcategorias,
}: HeaderTableProps) {
  const [modalAssinaturasOpen, setModalAssinaturasOpen] = useState(false);
  const onUnselect = () => onSelectOperacao?.(undefined);
  const { data: contasBancarias } = useGetContasBancarias({
    incluirInativas: true,
  });

  const [noPercentage, setNoPercentage] = useState(false);
  const [agruparPdfConta, setAgruparPdfConta] = useState(false);

  const {
    refetch,
    isLoading: isLoadingPdf,
    isFetching: isFetchingPdf,
  } = useRelatorioDemonstrativoReceitaDespesaPdf(filtro);

  const { onDownload: onExportPdf } = useDownloadRenderedPdf({
    fileName: `Relatório demonstrativo receita e despesa- ${moment().format(
      "DD_MM_YYYY"
    )}`,
    Component: PDFRelatorioDemonstrativoReceitaDespesa,
    loading: isLoadingPdf || isFetchingPdf,
  });

  const [loadingPdfMessage, setLoadingPdfMessage] = useState<
    string | undefined
  >();

  const _onExportPdf = () => setModalAssinaturasOpen(true);

  const onSubmitAssinaturas = (assinaturas: AssinaturasItem[]) => {
    if (!isLoadingPdf) {
      refetch().then(({ data }) => {
        if (data) {
          onExportPdf({
            data: { ...data, assinaturas },
            noPercentage,
            somarSubcategorias,
            operacao,
          });
        }
        setModalAssinaturasOpen(false);
      });
    }
  };

  const contaBancariaIdsArray = contasBancarias?.items.map((id) => id.id);

  const _onExportAgrupadoPdf = async (assinaturas: AssinaturasItem[]) => {
    try {
      setLoadingPdfMessage("Carregando dados...");
      const blobOrAttachmentList: (Blob | string)[] = [];

      for (let i = 0; i < contaBancariaIdsArray?.length!; i++) {
        const contaBancariaIds = [contaBancariaIdsArray![i]];

        const { data } =
          await http.get<RelatorioDemonstrativoReceitaDespesaPdfResponse>(
            "/v1/relatorios/financeiro/demonstrativo-receita-despesa/pdf",
            {
              params: { ...filtro, contaBancariaIds },
            }
          );

        const tituloConta = contasBancarias?.items.map((id) =>
          contaBancariaIdsArray![i] === id.id ? id.nome : ""
        );

        const result = {
          blob: await pdf(
            <PDFRelatorioDemonstrativoReceitaDespesa
              data={{ ...data, assinaturas: assinaturas }}
              titulo={tituloConta![i]}
              noPercentage={noPercentage}
              somarSubcategorias={somarSubcategorias}
              operacao={operacao}
            />
          ).toBlob(),
        };
        setLoadingPdfMessage(
          `Gerando Relatórios... \n Progresso: ${i}/${contaBancariaIdsArray?.length!}`
        );
        blobOrAttachmentList.push(result.blob);
      }

      const finalBlob = await mergePdfsFromBlobOrUrlList(blobOrAttachmentList);

      downloadLocalBlob(
        finalBlob,
        `Relatório demonstrativo receita e despesa- ${moment().format(
          "DD_MM_YYYY"
        )}`,
        "application/pdf"
      );
    } catch (err) {
      logError(err);
      showError(err as any);
    } finally {
      setLoadingPdfMessage(undefined);
    }
    setModalAssinaturasOpen(false);
  };

  const onExportarXlsx = () => {
    exportExcelRelatorioDemonstrativoReceitaDespesa({
      receitas,
      despesas,
      filtro: filtro!,
      totais,
      transferencias,
      somarSubcategorias,
    });
  };

  useLoading(!!loadingPdfMessage, loadingPdfMessage);

  return (
    <Flex flexDir="column">
      <Flex gridGap="16px" flexWrap="wrap">
        <Flex w="100%" maxW="350px">
          <InputSearch defaultValue={value} onSearch={(v) => onSearch(v)} />
        </Flex>

        <Flex flex={1} gridGap={4} flexWrap="wrap">
          <Flex flex={1} pt="4px">
            <Flex gridGap="16px">
              <SelectableButton
                selected={operacao === OperacaoEnum.Receita}
                onClick={() => onSelectOperacao?.(OperacaoEnum.Receita)}
                onUnselect={onUnselect}
                rounded="full"
                selectedColor={colors.blue300}
                fontSize="14px"
              >
                Receitas
              </SelectableButton>
              <SelectableButton
                selected={operacao === OperacaoEnum.Despesa}
                onClick={() => onSelectOperacao?.(OperacaoEnum.Despesa)}
                onUnselect={onUnselect}
                rounded="full"
                selectedColor={colors.green500}
                fontSize="14px"
              >
                Despesas
              </SelectableButton>
            </Flex>
          </Flex>

          <Flex>
            <ExportMenu
              onExportPdf={_onExportPdf}
              onExportarXlsx={onExportarXlsx}
              childrenComponent={
                <Checkbox
                  mr="auto"
                  w={140}
                  isChecked={somarSubcategorias}
                  onChange={() => setSomarSubcategorias?.(!somarSubcategorias)}
                >
                  Somar total das subcategorias
                </Checkbox>
              }
            />
          </Flex>
        </Flex>
      </Flex>
      <ModalAssinaturasRelatorio
        isOpenModal={modalAssinaturasOpen}
        onClose={() => setModalAssinaturasOpen(false)}
        onSubmit={onSubmitAssinaturas}
        agruparPdfConta={agruparPdfConta}
        onExportAgrupadoPdf={_onExportAgrupadoPdf}
        childrenComponent={
          <Flex flexDir="column" gap={4}>
            <Checkbox
              mr="auto"
              isChecked={agruparPdfConta}
              onChange={() => setAgruparPdfConta(!agruparPdfConta)}
            >
              Agrupar por conta bancária o resultado
            </Checkbox>

            <Checkbox
              mr="auto"
              isChecked={noPercentage}
              onChange={() => setNoPercentage(!noPercentage)}
            >
              Remover porcentagem
            </Checkbox>

            <Checkbox
              mr="auto"
              isChecked={somarSubcategorias}
              onChange={() => setSomarSubcategorias?.(!somarSubcategorias)}
            >
              Somar total das subcategorias
            </Checkbox>
          </Flex>
        }
      />
    </Flex>
  );
}

export default HeaderTable;
